import React, { useState } from 'react';
import { kpiAdminService } from '../../services/kpiAdminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';

export function KPILibrary() {
  const [systemsWithDefs, setSystemsWithDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSystems, setExpandedSystems] = useState({});
  const [expandedDefs, setExpandedDefs] = useState({});

  useAbortableFetch(({ isCancelled }) => {
    (async () => {
      try {
        setLoading(true);
        const definitions = await kpiAdminService.getAllDefinitionsWithRules();
        if (isCancelled()) return;

        const systemsMap = {};
        definitions.forEach(def => {
          if (!systemsMap[def.kpi_system_id]) {
            systemsMap[def.kpi_system_id] = {
              id: def.kpi_system_id,
              name: def.kpi_systems?.name || 'Unknown System',
              country: def.kpi_systems?.country,
              version: def.kpi_systems?.version || '1.0',
              definitions: [],
              is_active: true,
            };
          }
          systemsMap[def.kpi_system_id].definitions.push({
            ...def,
            rules: def.kpi_scoring_rules || [],
          });
        });

        setSystemsWithDefs(Object.values(systemsMap));
      } catch (err) {
        if (isCancelled()) return;
        setError(mapSupabaseError(err));
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    })();
  }, []);

  const toggleSystem = (systemId) => {
    setExpandedSystems(prev => ({
      ...prev,
      [systemId]: !prev[systemId],
    }));
  };

  const toggleDef = (defId) => {
    setExpandedDefs(prev => ({
      ...prev,
      [defId]: !prev[defId],
    }));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error-message" role="alert">{error}</div>;

  return (
    <div className="kpi-library">
      <h2>KPI Library Reference</h2>
      <p className="subtitle">Complete reference of all KPI systems, definitions, and scoring rules</p>

      {systemsWithDefs.map(system => (
        <div key={system.id} className="system-accordion">
          <h3 className="accordion-heading">
            <button
              type="button"
              className="accordion-header"
              aria-expanded={!!expandedSystems[system.id]}
              aria-controls={`system-panel-${system.id}`}
              onClick={() => toggleSystem(system.id)}
            >
              <span className="toggle-icon" aria-hidden="true">
                {expandedSystems[system.id] ? '▼' : '▶'}
              </span>
              <span className="accordion-title">{system.name}</span>
              <span className="system-meta">
                {system.country} • v{system.version} • {system.definitions.length} KPIs
              </span>
            </button>
          </h3>

          {expandedSystems[system.id] && (
            <div id={`system-panel-${system.id}`} className="accordion-content">
              {system.definitions.map(def => (
                <div key={def.id} className="definition-item">
                  <button
                    type="button"
                    className="definition-header"
                    aria-expanded={!!expandedDefs[def.id]}
                    aria-controls={`def-panel-${def.id}`}
                    onClick={() => toggleDef(def.id)}
                  >
                    <span className="toggle-icon" aria-hidden="true">
                      {expandedDefs[def.id] ? '▼' : '▶'}
                    </span>
                    <span className="definition-summary">
                      <strong>{def.kpi_code}</strong> — {def.kpi_name}
                      <span className="kpi-type">{def.kpi_type}</span>
                    </span>
                  </button>

                  {expandedDefs[def.id] && (
                    <div id={`def-panel-${def.id}`} className="definition-details">
                      {def.description && (
                        <div className="detail-row">
                          <strong className="detail-label">Description:</strong>
                          <span>{def.description}</span>
                        </div>
                      )}
                      {def.requirements && (
                        <div className="detail-row">
                          <strong className="detail-label">Requirements:</strong>
                          <span>{def.requirements}</span>
                        </div>
                      )}
                      {def.platform && (
                        <div className="detail-row">
                          <strong className="detail-label">Platform:</strong>
                          <span>{def.platform}</span>
                        </div>
                      )}

                      <div className="scoring-rules">
                        <h4>Scoring Rules</h4>
                        {def.rules.length === 0 ? (
                          <p className="no-data">No scoring rules defined</p>
                        ) : (
                          <table className="rules-table">
                            <thead>
                              <tr>
                                <th>Period</th>
                                <th>Target</th>
                                <th>Points</th>
                                <th>Bonus</th>
                                <th>Penalty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {def.rules.map(rule => (
                                <tr key={rule.id}>
                                  <td>{rule.period}</td>
                                  <td>{rule.target_value}</td>
                                  <td>{rule.points_for_achievement}</td>
                                  <td>{rule.bonus_points || 'N/A'}</td>
                                  <td>
                                    {rule.penalty_amount
                                      ? `${rule.penalty_amount} (${rule.penalty_description})`
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
